/**
 * Which parts the pipeline recognised, and which it could not.
 *
 * The redesigned case page showed only the tally — "Parts identified 12 of 15"
 * — while the old design listed both sets behind it. The names were in the
 * response the whole time; nothing rendered them.
 *
 * The API has never settled on one field name for either set, so each is read
 * from every spelling seen in a real response. A client that answers with any
 * one of them works; a client that answers with none produces an empty list
 * rather than a wrong one.
 *
 * Pure, so it can be tested without the page: this runner cannot load
 * CaseDetailsRedesign.
 */

const IDENTIFIED_FIELDS = [
  "identified_parts_list",
  "parts_identified_list",
  "identified_parts",
];

const UNIDENTIFIED_FIELDS = [
  "non_identified_parts_list",
  "unidentified_parts_list",
  "unidentified_parts",
];

const asArray = (value) => {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
};

/** Trimmed, de-duplicated, order preserved, empties dropped. */
const uniqueValues = (values) => {
  const seen = new Set();
  return values
    .map((value) => (value ?? "").toString().trim())
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const collect = (summary, fields) =>
  uniqueValues(fields.flatMap((field) => asArray(summary?.[field])));

/**
 * @returns {{identified: string[], unidentified: string[]}}
 */
export const partsOverview = (assessmentData) => {
  const summary = assessmentData?.summary || {};

  const unidentified = collect(summary, UNIDENTIFIED_FIELDS);
  const unidentifiedKeys = new Set(
    unidentified.map((part) => part.toLowerCase()),
  );

  // A part named in both sets is unidentified: the pipeline saying it could
  // not read something outranks a roster that merely lists it.
  const identified = collect(summary, IDENTIFIED_FIELDS).filter(
    (part) => !unidentifiedKeys.has(part.toLowerCase()),
  );

  return { identified, unidentified };
};

/** True when there is anything to show, so the row can stay inert if not. */
export const hasPartsOverview = (assessmentData) => {
  const { identified, unidentified } = partsOverview(assessmentData);
  return identified.length > 0 || unidentified.length > 0;
};

export default partsOverview;
